import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import { t } from 'i18next'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SuperCluster from 'supercluster'

import { notNullOrUndefined } from '../../helpers/utilis.js'
import { useDebounce } from '../../hooks/useDebounce.js'
import { getOptimalClusterRadius, getOptimalMinPoints } from '../../utils/mapPerformance.js'
import { useGlobalContext } from '../Shared/Contexts/GlobalContext.jsx'
import DeviceMarker from './DeviceMarker.jsx'

const isValidPosition = (position) => !!position && notNullOrUndefined(position.latitude) && notNullOrUndefined(position.longitude)

const Markers = ({ devices }) => {
  const navigate = useNavigate()
  const { setMapCenter, setMapZoom, devicesFilters: filters, setDevicesFilters, openSuccessAlert } = useGlobalContext()

  const [clusters, setClusters] = useState([])
  const [visibleMarkers, setVisibleMarkers] = useState([])
  const [mapState, setMapState] = useState({ bounds: null, zoom: null })

  const map = useMap()

  const { deviceId } = useParams()

  const debouncedMapState = useDebounce(mapState, 150)

  const superCluster = useMemo(() => {
    const radius = getOptimalClusterRadius(mapState.zoom || 10)
    const minPoints = getOptimalMinPoints(devices.length)

    return new SuperCluster({
      radius,
      maxZoom: 16,
      minZoom: 0,
      minPoints,
      extent: 256,
      nodeSize: 64
    })
  }, [devices.length, mapState.zoom])

  const geoJsonFeatures = useMemo(() => devices
    .filter(device => isValidPosition(device.position))
    .map(device => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [device.position.longitude, device.position.latitude]
      },
      properties: {
        deviceId: device.id,
        device: device
      }
    }))
  , [devices])

  useEffect(() => {
    if (!deviceId) {
      return
    }

    const device = devices.find(device => device.id === deviceId)
    if (!device || !device.position) {
      return
    }

    map.setCenter({ lat: device.position.latitude, lng: device.position.longitude })
    map.setZoom(22)

  }, [deviceId, devices])

  useEffect(() => {
    if (!geoJsonFeatures.length) {
      return
    }

    superCluster.load(geoJsonFeatures)
  }, [superCluster, geoJsonFeatures])

  useEffect(() => {
    if (!map || !geoJsonFeatures.length || !!deviceId) {
      return
    }

    if (geoJsonFeatures.length === 1) {
      const [lng, lat] = geoJsonFeatures[0].geometry.coordinates
      map.setCenter({ lat, lng })
      map.setZoom(16)
      return
    }

    const bounds = new window.google.maps.LatLngBounds()
    geoJsonFeatures.forEach(f => {
      const [lng, lat] = f.geometry.coordinates
      bounds.extend({ lat, lng })
    })

    map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 })
    const idleListener = map.addListener('idle', () => {
      idleListener.remove()
    })

  }, [devices])

  useEffect(() => {
    if (!map) {
      return
    }

    const updateMapState = () => {
      const bounds = map.getBounds()
      const zoom = map.getZoom()

      if (bounds && zoom !== undefined) {
        setMapState({ bounds, zoom })
      }
    }

    updateMapState()

    map.addListener('bounds_changed', updateMapState)
    map.addListener('zoom_changed', updateMapState)
  }, [map])

  useEffect(() => {
    if (!debouncedMapState.bounds || debouncedMapState.zoom === null || !geoJsonFeatures.length) {
      return
    }

    const { bounds, zoom } = debouncedMapState

    const bbox = [
      bounds.getSouthWest().lng(),
      bounds.getSouthWest().lat(),
      bounds.getNorthEast().lng(),
      bounds.getNorthEast().lat()
    ]

    const newClusters = superCluster.getClusters(bbox, Math.floor(zoom))
    setClusters(newClusters)

    const visible = newClusters
      .filter(cluster => !cluster.properties.cluster)
      .map(point => point.properties.device)

    setVisibleMarkers(visible)
  }, [debouncedMapState, superCluster, geoJsonFeatures])

  const handleDeviceClick = useCallback((device) => {
    const { id, position, isList } = device

    setMapCenter({
      lat: position.latitude,
      lng: position.longitude
    })

    setMapZoom({ zoom: 22 })

    if (isList) {
      setDevicesFilters({
        ...filters,
        nearby: {
          distance: 3,
          center: {
            latitude: position.latitude,
            longitude: position.longitude
          }
        }
      })
      openSuccessAlert(t('common.listFilterApplied'))
      setMapZoom({ zoom: 22 })
    } else {
      navigate(`/detail/${id}`)
    }
  }, [filters])

  return (
    <>
      {clusters
        .filter(cluster => cluster.properties.cluster)
        .map(cluster => (
          <ClusterMarker
            key={`cluster-${cluster.properties.cluster_id}`}
            cluster={cluster}
            superCluster={superCluster}
          />
        ))}

      {visibleMarkers.filter((device) => isValidPosition(device.position)).map(device => (
        <DeviceMarker
          deviceId={deviceId}
          key={device.id}
          device={device}
          onClick={handleDeviceClick}
        />
      ))}
    </>
  )
}

export const ClusterMarker = ({ cluster, superCluster }) => {
  const map = useMap()

  const [lng, lat] = cluster.geometry.coordinates
  const { point_count: pointCount } = cluster.properties

  const handleClusterClick = () => {
    if (!map || !window.google) {
      return
    }

    const { cluster_id, point_count } = cluster.properties
    const leaves = superCluster.getLeaves(cluster_id, point_count)

    if (!leaves || !leaves.length) {
      return
    }

    if (leaves.length === 1) {
      const [slng, slat] = leaves[0].geometry.coordinates
      map.setCenter({ lat: slat, lng: slng })
      map.setZoom(18)
      return
    }

    const bounds = new window.google.maps.LatLngBounds()
    leaves.forEach(leaf => {
      const [llng, llat] = leaf.geometry.coordinates
      bounds.extend({ lat: llat, lng: llng })
    })
    map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 })

  }

  const clusterIcon = useMemo(() => {
    const size = pointCount < 100 ? 30 : pointCount < 1000 ? 40 : 50
    const fontSize = pointCount < 100 ? '12px' : pointCount < 1000 ? '14px' : '16px'

    return (
      <div
        style={{
          backgroundColor: '#0f81fc',
          borderRadius: '50%',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: `${size}px`,
          height: `${size}px`,
          fontSize,
          fontWeight: 'bold',
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          cursor: 'pointer'
        }}
      >
        {pointCount}
      </div>
    )
  }, [pointCount])

  return (
    <AdvancedMarker
      position={{ lat, lng }}
      title={t('devices.clusterDevice', { count: pointCount })}
      aria-label={t('devices.clusterDevice', { count: pointCount })}
      onClick={handleClusterClick}
    >
      {clusterIcon}
    </AdvancedMarker>
  )
}

export default memo(Markers)
